/**
 * OS Targeting — data-layer client.
 *
 * Thin typed wrapper over `apiClient` for the
 * `/api/v1/campaign-os-targeting` surface (feature #69). Mirrors the
 * contract declared in `apps/api/src/routes/campaign-os-targeting.ts`.
 *
 * Keep this file dependency-free beyond the root `apiClient` — no React,
 * no TanStack, so it can be reused from server-side smoke harnesses.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Canonical OS codes — aligned with API ALLOWED_OS_CODES. The dash form only
// surfaces the four consumer-facing operating systems per spec
// (iOS / Android / Windows / macOS). "linux" / "other" stay in the API
// contract but are hidden from the form.
// ---------------------------------------------------------------------------

export const FORM_OS_CODES = ["ios", "android", "windows", "macos"] as const;
export type FormOsCode = (typeof FORM_OS_CODES)[number];

export const OS_LABELS: Record<FormOsCode, string> = {
	ios: "iOS",
	android: "Android",
	windows: "Windows",
	macos: "macOS",
};

export type TargetingMode = "allow" | "deny";

// ---------------------------------------------------------------------------
// API row shape (what the backend returns) — the `os_list` column is a JSON
// array of OS codes. The full API enum is wider than the four we expose in
// the form (it also permits "linux" and "other"). Preserve the full set here
// so we don't lose data silently on round-trip.
// ---------------------------------------------------------------------------

export const API_OS_CODES = [
	"ios",
	"android",
	"windows",
	"macos",
	"linux",
	"other",
] as const;
export type ApiOsCode = (typeof API_OS_CODES)[number];

export interface OsTargetingRow {
	id: string;
	tenant_id: string;
	campaign_id: string;
	os_list: ApiOsCode[];
	created_at: number;
	updated_at: number;
}

export interface OsTargetingResponse {
	targeting: OsTargetingRow;
}

// ---------------------------------------------------------------------------
// Deny-mode simulation: the backend only stores an allowlist. To model
// "deny" semantics from the form, the dash derives the effective allow-list
// by subtracting the chosen OS from all four consumer OS codes. This keeps
// the API clean while giving the operator the mental model they want.
// ---------------------------------------------------------------------------

export function formSelectionToApiList(
	mode: TargetingMode,
	selected: readonly FormOsCode[],
): FormOsCode[] {
	const unique = Array.from(new Set(selected)) as FormOsCode[];
	if (mode === "allow") return unique;
	return FORM_OS_CODES.filter((code) => !unique.includes(code));
}

/**
 * Reverse of `formSelectionToApiList`. Returns `null` when the stored list
 * contains anything outside the four form codes (e.g. "linux" / "other"),
 * which means a legacy row was created outside the dash — the form then
 * falls back to "allow" mode with the closest representation.
 */
export function apiListToFormSelection(
	list: readonly ApiOsCode[],
): { mode: TargetingMode; selected: FormOsCode[] } {
	const formCodes = new Set<FormOsCode>(FORM_OS_CODES);
	const cleaned = list.filter((c): c is FormOsCode => formCodes.has(c as FormOsCode));
	// If the stored list covers 3 of 4 form codes, deny-mode is the simpler
	// representation (operator likely denied the missing one).
	if (cleaned.length === FORM_OS_CODES.length - 1) {
		const denied = FORM_OS_CODES.filter((c) => !cleaned.includes(c));
		return { mode: "deny", selected: denied };
	}
	return { mode: "allow", selected: cleaned };
}

// ---------------------------------------------------------------------------
// Input validation — hand-rolled schema mirroring the Zod surface we would
// use. This keeps the dash zero-dep on zod/react-hook-form while preserving
// a familiar `.safeParse` shape for the UI layer.
// ---------------------------------------------------------------------------

export const MIN_VERSION_MAX_LEN = 32;

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface OsTargetingFormInput {
	campaignId: string;
	mode: TargetingMode;
	selected: FormOsCode[];
	minVersion?: string;
}

export interface OsTargetingFormParsed {
	campaignId: string;
	mode: TargetingMode;
	selected: FormOsCode[];
	minVersion: string | null;
}

const CAMPAIGN_ID_MAX = 120;

export const osTargetingFormSchema = {
	safeParse(input: OsTargetingFormInput): ParseResult<OsTargetingFormParsed> {
		const errors: Record<string, string> = {};

		const campaignId = input.campaignId?.trim() ?? "";
		if (campaignId.length === 0) {
			errors.campaignId = "Campaign ID is required.";
		} else if (campaignId.length > CAMPAIGN_ID_MAX) {
			errors.campaignId = `Campaign ID must be ${CAMPAIGN_ID_MAX} chars or fewer.`;
		} else if (/[\x00-\x1f]/.test(campaignId)) {
			errors.campaignId = "Campaign ID contains invalid control characters.";
		}

		if (input.mode !== "allow" && input.mode !== "deny") {
			errors.mode = "Mode must be allow or deny.";
		}

		const allowed = new Set<FormOsCode>(FORM_OS_CODES);
		const selected: FormOsCode[] = [];
		const seen = new Set<FormOsCode>();
		for (const code of input.selected ?? []) {
			if (!allowed.has(code)) {
				errors.selected = "Unsupported operating system in selection.";
				break;
			}
			if (!seen.has(code)) {
				seen.add(code);
				selected.push(code);
			}
		}
		if (!errors.selected && selected.length === 0) {
			errors.selected = "Pick at least one operating system.";
		}

		let minVersion: string | null = null;
		if (typeof input.minVersion === "string") {
			const trimmed = input.minVersion.trim();
			if (trimmed.length > 0) {
				if (trimmed.length > MIN_VERSION_MAX_LEN) {
					errors.minVersion = `Minimum version must be ${MIN_VERSION_MAX_LEN} chars or fewer.`;
				} else if (!/^[0-9][0-9A-Za-z.\-_]*$/.test(trimmed)) {
					errors.minVersion = "Use a numeric version like 14.0 or 33.";
				} else {
					minVersion = trimmed;
				}
			}
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				campaignId,
				mode: input.mode,
				selected,
				minVersion,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Remote operations — TanStack Query consumers import these directly.
// ---------------------------------------------------------------------------

const BASE = "/campaign-os-targeting";

/**
 * Returns the targeting row for a campaign, or `null` if none exists yet.
 * 404 from the API is normalized to `null` so the query layer can render
 * an empty form without an error state.
 */
export async function fetchOsTargeting(
	campaignId: string,
): Promise<OsTargetingRow | null> {
	try {
		const res = await apiClient.get<OsTargetingResponse>(
			`${BASE}/${encodeURIComponent(campaignId)}`,
		);
		return res.targeting;
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

export interface SaveOsTargetingArgs {
	campaignId: string;
	mode: TargetingMode;
	selected: FormOsCode[];
	/** Minimum OS version is a UI-only hint — persisted via PUT body. */
	minVersion?: string | null;
}

/**
 * Upsert an OS targeting row for the campaign. Uses PUT against the
 * `:campaignId` endpoint, which creates-or-replaces atomically.
 */
export async function saveOsTargeting(
	args: SaveOsTargetingArgs,
): Promise<OsTargetingRow> {
	const osList = formSelectionToApiList(args.mode, args.selected);
	const res = await apiClient.put<OsTargetingResponse>(
		`${BASE}/${encodeURIComponent(args.campaignId)}`,
		{ os: osList },
	);
	return res.targeting;
}

/** Remove the targeting row. Silently no-ops on 404. */
export async function deleteOsTargeting(campaignId: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/${encodeURIComponent(campaignId)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export const osTargetingQueryKeys = {
	all: ["os-targeting"] as const,
	byCampaign: (campaignId: string) =>
		[...osTargetingQueryKeys.all, campaignId] as const,
};
